// Auth context for the BARK SPA. (BAR-40 route-tree overhaul)
//
// Lifts the auth slice that the top-level App owns today
// (isAuthenticated / authToken / userData / reset+invite tokens + the
// login/logout handlers) out of prop-drilling and into a context the
// route tree consumes. Split into STATE and ACTIONS contexts so a token
// refresh (state change) never re-renders consumers that only need the
// stable handlers.
//
// Stage 0: contracts + typed hooks only. The provider is a thin value
// pass-through; App computes the value from its existing state in Stage 1
// (so App keeps owning the state — lower risk than physically relocating
// it — while the tree consumes it via these hooks instead of props).
import React, { createContext, useContext } from 'react';

/** The logged-in user object (loosely shaped in the legacy code). */
export interface AuthUser {
  email?: string | null;
  name?: string | null;
  uid?: string | null;
  id?: string | null;
  is_admin?: boolean;
  company?: string | null;
  disabled_modules?: string[];
  /** `free` accounts get Chat from the chat-free service (when it is configured). */
  plan?: 'free' | 'full';
  [key: string]: unknown;
}

/** BAR-135: active super-admin impersonation session metadata, mirrored from
 *  the mint response + persisted to this tab's `bark_impersonation` so the
 *  banner and refresh-suppression survive a page reload. `null` when not
 *  impersonating. */
export interface ImpersonationMeta {
  session_id: string;
  /** ISO-8601 session ceiling (mint + 60 min, no extension). */
  expires_at: string;
  /** The real super_admin's email (shown for audit context). */
  actor_email: string;
  /** The impersonated target's email (shown in the banner). */
  target_email: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  authToken: string | null;
  userData: AuthUser | null;
  /** Password-reset deep-link token (stripped from the URL on mount). */
  resetToken: string | null;
  /** Invite deep-link token (BAR-126). */
  inviteToken: string | null;
  /** BAR-1528: free-signup activation deep-link token (`?signup_token=`).
   *  Optional so legacy provider stubs keep compiling; App always supplies it. */
  signupToken?: string | null;
  /** BAR-135: non-null while a super_admin is impersonating a user. Optional so
   *  legacy provider stubs (tests/sandboxes) keep compiling; App always supplies it. */
  impersonation?: ImpersonationMeta | null;
  /** BAR-962: a `?connect=<state>` connector sign-in is in progress (waiting
   *  for sign-in or consent). While it is, Home does not send a free account
   *  to `/connect`. Optional for the same reason as `signupToken`. */
  connectPending?: boolean;
  /** BAR-1564: who signs people in (`pending` until config.json is read).
   *  Optional so legacy provider stubs keep compiling; absent means Firebase. */
  authMode?: 'firebase' | 'keycloak' | 'pending';
  /** BAR-1564: the waiting connector hand-off as a path (`/?connect=…`), so a
   *  Keycloak sign-in returns to it. Null when none waits. */
  connectReturnPath?: string | null;
  /** BAR-1564: why the last Keycloak sign-in did not finish, for the sign-in page. */
  loginNotice?: string | null;
  /** BAR-1564: how the notice reads — 'info' for a failed sign-out (news, not a fault). */
  loginNoticeTone?: 'error' | 'info';
  /** BAR-1564: after BARK refused an account, or a Keycloak sign-out that
   *  could not start or has not finished, the next Keycloak sign-in asks for
   *  credentials even if Keycloak still has a session. Kept across reloads
   *  until a sign-in BARK accepts. */
  forceLoginPrompt?: boolean;
  /** BAR-1564: true until App's startup restore (including the /auth/callback
   *  path) has finished. The automatic Keycloak redirect waits for it, so a
   *  session that can still be restored is never sent to sign in again.
   *  Optional so legacy provider stubs keep compiling; App always supplies it.
   *  Absent counts as still running (fails closed), so a stub never sends
   *  anyone to Keycloak. */
  sessionRestorePending?: boolean;
  /** BAR-1564: true from the moment a person's own sign-out starts in
   *  keycloak mode. The browser is about to leave for Keycloak's sign-out,
   *  so the sign-in page must not start Keycloak's sign-in (that navigation
   *  could cancel the sign-out and sign the person straight back in).
   *  Optional so provider stubs keep compiling; absent means no sign-out is
   *  under way. */
  signingOut?: boolean;
}

export interface AuthActions {
  handleLoginSuccess: (token: string, userData?: AuthUser, refreshToken?: string) => void;
  handleLogout: () => void | Promise<void>;
  setResetToken: (v: string | null) => void;
  setInviteToken: (v: string | null) => void;
  /** BAR-1528: clears the activation token when its modal closes. Optional
   *  for the same reason as `signupToken`. */
  setSignupToken?: (v: string | null) => void;
  /** BAR-1564: the sign-in page's sign-out view ran out while Keycloak's
   *  sign-out is still on its way: from then on the next Keycloak sign-in
   *  asks for credentials (`forceLoginPrompt`). Optional so provider stubs
   *  keep compiling. */
  handleSignOutLate?: () => void;
  /** BAR-82: whether a module (e.g. 'build_artifact') is disabled for this
   *  user's company — derives from userData.disabled_modules. */
  isModuleDisabled: (moduleId: string) => boolean;
  /** Chat is served by chat-free for this account (free plan + the service is
   *  configured). Optional so provider stubs keep compiling; absent = false. */
  usesChatFree?: boolean;
  /** BAR-135: start impersonating a target user (super_admin only). Mints the
   *  target token, stashes the real admin trio, swaps session state, and
   *  routes home. Optional so legacy provider stubs keep compiling. */
  startImpersonation?: (targetUid: string, reason?: string) => Promise<void>;
  /** BAR-135: exit impersonation — refresh + restore the stashed real admin,
   *  end the session server-side, and route home. Optional (see above). */
  exitImpersonation?: () => Promise<void>;
}

const AuthStateContext = createContext<AuthState | undefined>(undefined);
const AuthActionsContext = createContext<AuthActions | undefined>(undefined);

export function AuthProvider({
  state,
  actions,
  children,
}: {
  state: AuthState;
  actions: AuthActions;
  children: React.ReactNode;
}) {
  return (
    <AuthActionsContext.Provider value={actions}>
      <AuthStateContext.Provider value={state}>{children}</AuthStateContext.Provider>
    </AuthActionsContext.Provider>
  );
}

/** The no-throw variant for components that may render without a
 * provider (admin panels in tests) — undefined instead of an exception,
 * so the hook stays unconditional and lint-clean (PR #599). */
export function useOptionalAuth(): AuthState | undefined {
  return useContext(AuthStateContext);
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthStateContext);
  if (ctx === undefined) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export function useAuthActions(): AuthActions {
  const ctx = useContext(AuthActionsContext);
  if (ctx === undefined) throw new Error('useAuthActions must be used within <AuthProvider>');
  return ctx;
}
